import React, {useEffect, useReducer} from "react";
import {initialButtonState, ScrubInvokeReducer} from "../../commons/reducer/ScrubInvokeReducer";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import {Translation, useTranslation} from "./Translation";
import Button from "@amzn/meridian/button";
import {
    BUTTON_DISABLE_KEY_PREFIX,
    BUTTON_DISABLE_TIME,
    SCRUB_BUTTON_TYPE, STATION_CODE
} from "../../commons/constants/ScrubConstants";
import {ConfirmBox} from "./ConfirmBox";
import {useTimeLeft} from "./TimeLeft";
import {useLocalStorage} from "./LocalStorage";
import Row from "@amzn/meridian/row";
import Loader from "@amzn/meridian/loader";
import {ScrubEligibilityReducer} from "../../commons/reducer/ScrubEligibilityReducer";
import {useDeviceLayout} from "../../commons/context/DeviceLayoutContext";
import {getStationCode} from "../../util/CommonUtils";

const STATES = {
    IDLE: "idle",
    LOADING: "loading",
    CONFIRM: "confirm",
    SUCCESS: "success",
    ERROR: "error",
    WARNING: "warning",
    DISABLED: "disabled"
};

const ELIGIBILITY_STATUS = {
    ERROR: "ERROR",
    WARNING: "WARNING",
    SUCCESS: "SUCCESS"
};

const initialState = {
    eligibilityStatus: ELIGIBILITY_STATUS.ERROR,
    metaData: {},
    dispatchCheckStatus: STATES.IDLE
};

export const ScrubInvokeButton = ({view}) => {

    const [{status, enable,  metaDataInvocation}, dispatch] = useReducer(ScrubInvokeReducer, initialButtonState);
    const [{eligibilityStatus, metaData, dispatchCheckStatus}, scrubDispatch] = useReducer(ScrubEligibilityReducer, initialState);
    const modalHeading = useTranslation({stringId: SCRUB_BUTTON_TYPE[view].button});
    const [storedValue, setValue] = useLocalStorage(BUTTON_DISABLE_KEY_PREFIX + view, new Date().getTime());
    const [storedStationValue, setStationValue] = useLocalStorage(STATION_CODE , getStationCode().stationCode);

    const minutes = useTimeLeft(parseInt(storedValue, 10));
    const {isMobile} = useDeviceLayout();

    const dispatchStatus = (target, scrubType) => {
        dispatch({
            type: "SET_STATUS",
            data: target
        });
        checkScrubStartEligibility(scrubType);
    };
    const checkScrubStartEligibility = (scrubType) => {
        if (dispatchCheckStatus === STATES.IDLE && scrubType !== undefined) {
            scrubDispatch({
                type: "CHECK_ELIGIBILITY",
                data: {
                    dispatcher: scrubDispatch,
                    scrubType: scrubType
                }
            })
        }
    };
    const startScrub = () => dispatch({
        type: "START_SCRUB",
        data: {
            dispatcher: dispatch,
            scrubType: SCRUB_BUTTON_TYPE[view].scrubType
        }
    });

    useEffect(() => {
        if (status === STATES.SUCCESS) {
            const currentTime = new Date().getTime();
            setValue(JSON.stringify((new Date(currentTime + BUTTON_DISABLE_TIME).getTime())));
            setStationValue(getStationCode().stationCode);

        }
    }, [status])

    useEffect(() => {
        const remainingTime = (storedValue - new Date().getTime());
        if (remainingTime >= 0 && remainingTime <= BUTTON_DISABLE_TIME && enable && storedStationValue === getStationCode().stationCode) {
            dispatch({type: "TOGGLE_BUTTON"})
            setTimeout(function () {
                dispatch({type: "TOGGLE_BUTTON"})
            }, remainingTime);
        }
    })


    return (
        <React.Fragment>
            <Button size={isMobile ? "small" : "medium"} id={view} disabled={[STATES.LOADING].includes(status) || !enable}
                    onClick={() => dispatchStatus(STATES.CONFIRM, SCRUB_BUTTON_TYPE[view].scrubType)}>
                {
                    (!enable)
                        ? <Translation
                            stringId="scrub_invoke_button_enable_message"
                            placeholder={[["X", minutes]]}/>
                        : (<Translation key={status} stringId={SCRUB_BUTTON_TYPE[view].button}/>)
                }
            </Button>
            {
                [STATES.SUCCESS, STATES.WARNING]
                    .filter(s => s === status)
                    .map(s => (<AlertMessage id={view} type={s} key={s} onClose={() => dispatchStatus(STATES.IDLE)}/>))
            }

            {
                //add popup for ERROR: unauthorised user in case of SSD EOD Scrub trigger
                [STATES.ERROR]
                    .filter(s => s === status)
                    .map(s => (<ConfirmBox open={status === STATES.ERROR}
                        onCancel={() => dispatchStatus(STATES.IDLE)}
                        confirmDisabled={true}
                        title={modalHeading}>
                        { metaDataInvocation!==null && metaDataInvocation.messageStringId !== null &&
                            <Row alignmentHorizontal="center" alignmentVertical="center">
                                <Alert type="error" size="small"><Translation stringId={metaDataInvocation.messageStringId}/></Alert>
                            </Row>}
                        { (metaDataInvocation===null || metaDataInvocation.messageStringId === null) &&
                            <Row alignmentHorizontal="center" alignmentVertical="center">
                                <Alert type="error" size="small"><Translation stringId={view + "_" + s}/></Alert>

                            </Row>}
                    </ConfirmBox>))

            }
            <ConfirmBox
                title={modalHeading}
                open={status === STATES.CONFIRM}
                onConfirm={() => startScrub()}
                onCancel={() => dispatchStatus(STATES.IDLE)}
                confirmDisabled={eligibilityStatus === ELIGIBILITY_STATUS.ERROR}
                scrubType={SCRUB_BUTTON_TYPE[view].scrubType}>
                <div>
                    {dispatchCheckStatus === STATES.LOADING &&
                    <Row alignmentHorizontal="center" alignmentVertical="center"><Loader/></Row>}

                    {eligibilityStatus === ELIGIBILITY_STATUS.WARNING && dispatchCheckStatus === STATES.SUCCESS &&
                    metaData.messageStringId &&
                    <Row alignmentHorizontal="center" alignmentVertical="center">
                        <Alert type="warning" size="small"><Translation stringId={metaData.messageStringId}/></Alert>
                    </Row>
                    }
                    {eligibilityStatus === ELIGIBILITY_STATUS.ERROR && dispatchCheckStatus === STATES.SUCCESS &&
                    <Row alignmentHorizontal="center" alignmentVertical="center">
                        <Alert type="error" size="small"><Translation stringId={metaData.messageStringId}/></Alert>
                    </Row>
                    }
                    {eligibilityStatus !== ELIGIBILITY_STATUS.ERROR && dispatchCheckStatus === STATES.SUCCESS &&
                    <Text type="b500" alignment="center"><Translation stringId={view + '_confirm'}/></Text>}
                </div>
            </ConfirmBox>

        </React.Fragment>
    )
}

const AlertMessage = ({id, type, onClose}) => {
    return (<div className="alert-message">
        <Alert type={type} size="medium" width="100%" onClose={onClose}>
            <Translation stringId={id + '_' + type}/>
        </Alert>
    </div>);
};



// WEBPACK FOOTER //
// ./src/components/scrub/ScrubInvokeButton.js